import React from "react";
import { Smartphone, X } from "lucide-react";
import { StreamInfo } from "../utils/streams";
import { ActivationTarget } from "../utils/activation";

interface ActivationChooserProps {
  target: ActivationTarget;
  streams: StreamInfo[];
  onPick: (streamName: string) => void;
  onCancel: () => void;
}

/** Shown when the app (or Chrome extension) sent someone here to sign in and their account has more than one stream. */
const ActivationChooser: React.FC<ActivationChooserProps> = ({ target, streams, onPick, onCancel }) => (
  <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6" role="dialog" aria-modal="true" aria-labelledby="activation-title">
    <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={onCancel} />
    <div className="bg-white rounded-[2rem] shadow-2xl w-full max-w-md p-8 relative">
      <button onClick={onCancel} aria-label="Close" className="absolute top-6 right-6 text-slate-400 hover:text-slate-600 transition-colors">
        <X size={20} />
      </button>
      <div className="flex flex-col items-center text-center space-y-2 mb-6">
        <div className="p-4 bg-blue-50 rounded-2xl text-blue-600"><Smartphone size={24} /></div>
        <h3 id="activation-title" className="text-xl font-bold text-slate-900">Which stream do you want to use?</h3>
        <p className="text-slate-500 text-sm">
          {target.kind === "app" ? "Choose the stream to open on your device." : "Choose the stream to open in the Chrome extension."}
        </p>
      </div>
      <ul className="space-y-2 max-h-80 overflow-y-auto">
        {streams.map((s) => (
          <li key={s.name}>
            <button
              onClick={() => onPick(s.name)}
              className="w-full flex items-center gap-3 p-3 rounded-2xl border border-slate-200 hover:border-blue-500 hover:bg-blue-50 transition-colors text-left"
            >
              {s.imageUrl ? (
                <img src={s.imageUrl} alt="" className="w-10 h-10 rounded-xl object-contain bg-slate-50" />
              ) : (
                <span className="w-10 h-10 rounded-xl" style={{ backgroundColor: s.color || "#888888" }} />
              )}
              <span className="font-semibold text-slate-800 break-all">{s.name}</span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  </div>
);

export default ActivationChooser;
